import { useCallback, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

interface ContactSuggestion {
  id: number;
  display_name: string;
  company: string | null;
}

interface PriorRow {
  id: number;
  meeting_date: string;
  meeting_title: string | null;
  created_at: string;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; id: number; meeting_date: string; meeting_title: string | null; contact_name: string; prior_rows: PriorRow[] }
  | { kind: 'duplicate'; id: number; meeting_date: string; meeting_title: string | null }
  | { kind: 'error'; message: string };

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

const TRANSCRIPT_EXTS = ['.srt', '.txt', '.docx', '.pdf'];
const SUMMARY_EXTS = ['.md', '.txt', '.docx', '.pdf'];

function acceptStr(exts: string[]): string {
  return exts.join(',');
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fileReady(file: File | null): string {
  return file ? file.name : 'Not attached';
}

// ──────────────────────────────────────────────────────────────────────────
// FileDrop — drag-and-drop or click-to-browse file input
// ──────────────────────────────────────────────────────────────────────────

interface FileDropProps {
  label: string;
  accept: string;
  required?: boolean;
  file: File | null;
  onFile: (f: File | null) => void;
  hint?: string;
}

function FileDrop({ label, accept, required, file, onFile, hint }: FileDropProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) onFile(dropped);
    },
    [onFile],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onFile(e.target.files?.[0] ?? null);
    },
    [onFile],
  );

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {hint && <p className="text-xs text-slate-500 mb-1">{hint}</p>}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition ${
          dragging
            ? 'border-slate-900 bg-slate-50'
            : file
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-slate-300 bg-white hover:border-slate-400'
        }`}
      >
        {file ? (
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm font-medium text-emerald-700 truncate max-w-xs">{file.name}</span>
            <button
              type="button"
              className="text-xs text-rose-500 underline shrink-0"
              onClick={(e) => { e.stopPropagation(); onFile(null); if (inputRef.current) inputRef.current.value = ''; }}
            >
              Remove
            </button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Drop file here or <span className="underline text-slate-700">click to browse</span>
          </p>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={handleChange}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ContactTypeahead
// ──────────────────────────────────────────────────────────────────────────

interface ContactTypeaheadProps {
  selected: ContactSuggestion | null;
  onSelect: (c: ContactSuggestion | null) => void;
}

function ContactTypeahead({ selected, onSelect }: ContactTypeaheadProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback((q: string) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    fetch(`/api/contacts?q=${encodeURIComponent(q)}&limit=8`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data: { contacts: ContactSuggestion[] }) => {
        setSuggestions(data.contacts ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (selected) onSelect(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const pickContact = (c: ContactSuggestion) => {
    onSelect(c);
    setQuery(`${c.display_name}${c.company ? ` (${c.company})` : ''}`);
    setSuggestions([]);
  };

  const clearContact = () => {
    onSelect(null);
    setQuery('');
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-1">
        Contact <span className="text-rose-500">*</span>
      </label>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInput}
          placeholder="Search by name or company..."
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          autoComplete="off"
        />
        {selected && (
          <button
            type="button"
            onClick={clearContact}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
          >
            Clear
          </button>
        )}
      </div>
      {loading && (
        <p className="text-xs text-slate-400 mt-1 px-1">Searching...</p>
      )}
      {suggestions.length > 0 && !selected && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => pickContact(c)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus:bg-slate-100 focus:outline-none"
              >
                <span className="font-medium text-slate-900">{c.display_name}</span>
                {c.company && <span className="text-slate-500 ml-1">({c.company})</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Main view
// ──────────────────────────────────────────────────────────────────────────

export function UploadTranscriptView(): JSX.Element {
  const [transcriptFile, setTranscriptFile] = useState<File | null>(null);
  const [summaryFile, setSummaryFile] = useState<File | null>(null);
  const [contact, setContact] = useState<ContactSuggestion | null>(null);
  const [meetingDate, setMeetingDate] = useState(todayIso());
  const [meetingTitle, setMeetingTitle] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const [archivingIds, setArchivingIds] = useState<Set<number>>(new Set());
  const [archivedIds, setArchivedIds] = useState<Set<number>>(new Set());

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!transcriptFile) {
      setSubmitState({ kind: 'error', message: 'Transcript file is required.' });
      return;
    }
    if (!contact) {
      setSubmitState({ kind: 'error', message: 'Please select a contact.' });
      return;
    }
    if (!meetingDate) {
      setSubmitState({ kind: 'error', message: 'Meeting date is required.' });
      return;
    }

    setSubmitState({ kind: 'submitting' });

    const form = new FormData();
    form.append('transcript', transcriptFile);
    if (summaryFile) form.append('summary', summaryFile);
    form.append('contact_id', String(contact.id));
    form.append('meeting_date', meetingDate);
    if (meetingTitle.trim()) form.append('meeting_title', meetingTitle.trim());

    try {
      const res = await fetch('/api/transcripts', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });

      const data = (await res.json()) as Record<string, unknown>;

      if (res.status === 200 && data.already_uploaded === true) {
        setSubmitState({
          kind: 'duplicate',
          id: data.id as number,
          meeting_date: data.meeting_date as string,
          meeting_title: (data.meeting_title as string | null) ?? null,
        });
        return;
      }

      if (res.status === 201 && data.already_uploaded === false) {
        setSubmitState({
          kind: 'success',
          id: data.id as number,
          meeting_date: data.meeting_date as string,
          meeting_title: (data.meeting_title as string | null) ?? null,
          contact_name: contact.display_name,
          prior_rows: (data.prior_rows as PriorRow[]) ?? [],
        });
        return;
      }

      if (res.status === 413) {
        setSubmitState({ kind: 'error', message: 'File too large. Maximum 5 MB per file.' });
        return;
      }

      const msg =
        typeof data.error === 'string'
          ? data.error
          : `Upload failed (status ${res.status})`;
      setSubmitState({ kind: 'error', message: msg });
    } catch (err) {
      setSubmitState({ kind: 'error', message: (err as Error).message });
    }
  };

  const handleArchive = async (id: number) => {
    setArchivingIds((prev) => new Set(prev).add(id));
    try {
      const res = await fetch(`/api/transcripts/${id}/archive`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (res.ok) {
        setArchivedIds((prev) => new Set(prev).add(id));
      } else {
        const data = (await res.json()) as { error?: string };
        alert(data.error ?? 'Archive failed');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setArchivingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleReset = () => {
    setTranscriptFile(null);
    setSummaryFile(null);
    setContact(null);
    setMeetingDate(todayIso());
    setMeetingTitle('');
    setSubmitState({ kind: 'idle' });
    setArchivingIds(new Set());
    setArchivedIds(new Set());
  };

  // ── Success state ────────────────────────────────────────────────────────
  if (submitState.kind === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
        <div className="mx-auto max-w-3xl rounded-lg bg-white p-6 ring-1 ring-slate-200">
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Intake complete
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">
              Transcript uploaded
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              The transcript is attached to the customer record. Extracted work stages in
              Plaud review before it becomes a real follow-up.
            </p>
          </div>

          <dl className="mb-6 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">Contact</dt>
              <dd className="mt-1 font-medium text-slate-950">{submitState.contact_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">Meeting date</dt>
              <dd className="mt-1 font-medium text-slate-950">{submitState.meeting_date}</dd>
            </div>
            {submitState.meeting_title ? (
              <div>
                <dt className="text-xs font-medium text-slate-500">Title</dt>
                <dd className="mt-1 font-medium text-slate-950">{submitState.meeting_title}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-medium text-slate-500">Transcript ID</dt>
              <dd className="mt-1 font-medium text-slate-950">#{submitState.id}</dd>
            </div>
          </dl>

          {submitState.prior_rows.length > 0 ? (
            <div className="mb-6 rounded-lg bg-amber-50 p-4 ring-1 ring-amber-200">
              <p className="mb-2 text-sm font-medium text-amber-800">
                Prior transcript versions found for this contact and date
              </p>
              <ul className="space-y-2">
                {submitState.prior_rows.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-col gap-2 rounded-md bg-white px-3 py-2 text-sm ring-1 ring-amber-100 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="text-slate-700">
                      {row.meeting_title ?? `Meeting on ${row.meeting_date}`}
                      <span className="ml-2 text-xs text-slate-400">#{row.id}</span>
                    </span>
                    {archivedIds.has(row.id) ? (
                      <span className="text-xs font-medium text-emerald-700">Archived</span>
                    ) : (
                      <button
                        type="button"
                        disabled={archivingIds.has(row.id)}
                        onClick={() => handleArchive(row.id)}
                        className="text-left text-xs font-medium text-amber-800 underline hover:text-amber-950 disabled:opacity-50 sm:text-right"
                      >
                        {archivingIds.has(row.id) ? 'Archiving...' : 'Archive old version'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Link
              to="/plaud/review"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Review staged work
            </Link>
            <button
              type="button"
              onClick={handleReset}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Upload another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Duplicate state ──────────────────────────────────────────────────────
  if (submitState.kind === 'duplicate') {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
        <div className="mx-auto max-w-2xl rounded-lg bg-white p-6 ring-1 ring-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
            Duplicate detected
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-950">Already uploaded</h1>
          <p className="mt-1 text-sm text-slate-600">
            This exact transcript is already in the evidence store.
          </p>
          <dl className="my-6 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">Meeting date</dt>
              <dd className="mt-1 font-medium text-slate-950">{submitState.meeting_date}</dd>
            </div>
            {submitState.meeting_title ? (
              <div>
                <dt className="text-xs font-medium text-slate-500">Title</dt>
                <dd className="mt-1 font-medium text-slate-950">{submitState.meeting_title}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-medium text-slate-500">Transcript ID</dt>
              <dd className="mt-1 font-medium text-slate-950">#{submitState.id}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/plaud/review"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Check review queue
            </Link>
            <button
              type="button"
              onClick={handleReset}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Upload a different transcript
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  const isSubmitting = submitState.kind === 'submitting';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Evidence intake
            </p>
            <h1 className="text-2xl font-semibold text-slate-950">
              Upload Plaud transcript
            </h1>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Attach the transcript to the customer, then send extracted work to Plaud
              review. Uploading stores evidence; review decides what becomes operational truth.
            </p>
          </div>
          <Link
            to="/plaud/review"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-center text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto"
          >
            Open review queue
          </Link>
        </header>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <form
            onSubmit={(e) => { void handleSubmit(e); }}
            className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200"
          >
            <div className="mb-4 border-b border-slate-100 pb-4">
              <h2 className="text-sm font-semibold text-slate-950">Transcript intake</h2>
              <p className="mt-1 text-xs text-slate-500">
                Required fields are the transcript export, customer, and meeting date.
              </p>
            </div>

            <div className="space-y-5">
              <FileDrop
                label="Plaud transcript export"
                accept={acceptStr(TRANSCRIPT_EXTS)}
                required
                file={transcriptFile}
                onFile={setTranscriptFile}
                hint="Supported: .srt, .txt, .docx, .pdf — max 5 MB"
              />

              <FileDrop
                label="Plaud AI summary"
                accept={acceptStr(SUMMARY_EXTS)}
                file={summaryFile}
                onFile={setSummaryFile}
                hint="Optional. Supported: .md, .txt, .docx, .pdf — max 5 MB"
              />

              <ContactTypeahead selected={contact} onSelect={setContact} />

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="meeting-date" className="mb-1 block text-sm font-medium text-slate-700">
                    Meeting date <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="meeting-date"
                    type="date"
                    value={meetingDate}
                    max={todayIso()}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>

                <div>
                  <label htmlFor="meeting-title" className="mb-1 block text-sm font-medium text-slate-700">
                    Meeting title
                  </label>
                  <input
                    id="meeting-title"
                    type="text"
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    placeholder="Discovery call - Sprinter build"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>

              {submitState.kind === 'error' ? (
                <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
                  {submitState.message}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-50 sm:w-auto"
              >
                {isSubmitting ? 'Uploading...' : 'Upload transcript'}
              </button>
            </div>
          </form>

          <aside className="space-y-4">
            <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <h2 className="text-sm font-semibold text-slate-950">Ready check</h2>
              <dl className="mt-3 space-y-3 text-sm">
                <div>
                  <dt className="text-xs font-medium text-slate-500">Transcript</dt>
                  <dd className="mt-1 truncate text-slate-900">{fileReady(transcriptFile)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">AI summary</dt>
                  <dd className="mt-1 truncate text-slate-900">{fileReady(summaryFile)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">Customer</dt>
                  <dd className="mt-1 truncate text-slate-900">
                    {contact ? contact.display_name : 'Not selected'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">Meeting date</dt>
                  <dd className="mt-1 text-slate-900">{meetingDate || 'Not set'}</dd>
                </div>
              </dl>
            </section>

            <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <h2 className="text-sm font-semibold text-slate-950">After upload</h2>
              <ol className="mt-3 space-y-3 text-sm text-slate-600">
                <li>
                  <span className="font-medium text-slate-900">1. Evidence attaches</span>
                  <br />
                  The transcript is linked to the selected contact.
                </li>
                <li>
                  <span className="font-medium text-slate-900">2. Candidates stage</span>
                  <br />
                  Extracted work waits in Plaud review.
                </li>
                <li>
                  <span className="font-medium text-slate-900">3. Human promotes</span>
                  <br />
                  Only promoted items become real follow-ups.
                </li>
              </ol>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
